import { getDepositMaxValue } from '@/domain/action-max-value-getters/getDepositMaxValue'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { depositValidationIssueToMessage, validateDeposit } from '@/domain/market-validators/validateDeposit'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { UseFormReturn } from 'react-hook-form'
import { AssetInputSchema } from '../../common/logic/form'
import { FormFieldsForDialog } from '../../common/types'
import { getCanonicalSymbol } from '@/domain/hooks/useDisplayToken'

export function getDepositDialogFormValidator(walletInfo: MarketWalletInfo, marketInfo: MarketInfo) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedUnitNumber(field.value === '' ? '0' : field.value)
    const canonicalSymbol = getCanonicalSymbol(marketInfo.chainId, field.symbol)
    const balance = walletInfo.findWalletBalanceForSymbol(canonicalSymbol)
    const supplyingReserve = marketInfo.findOneReserveBySymbol(canonicalSymbol)

    const issue = validateDeposit({
      value,
      asset: {
        status: supplyingReserve.status,
        totalLiquidity: supplyingReserve.totalLiquidity,
        supplyCap: supplyingReserve.supplyCap,
      },
      user: { balance, alreadyDepositedValueUSD: NormalizedUnitNumber('0') },
    })
    if (issue) {
      ctx.addIssue({
        code: 'custom',
        message: depositValidationIssueToMessage[issue],
        path: ['value'],
      })
    }
  })
}

export interface GetFormFieldsForDepositDialogArgs {
  form: UseFormReturn<AssetInputSchema>
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
}
export function getFormFieldsForDepositDialog({
  form,
  marketInfo,
  walletInfo,
}: GetFormFieldsForDepositDialogArgs): FormFieldsForDialog {
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const canonicalSymbol = getCanonicalSymbol(marketInfo.chainId, TokenSymbol(symbol))
  const position = marketInfo.findOnePositionBySymbol(canonicalSymbol)

  const maxValue = getDepositMaxValue({
    asset: {
      status: position.reserve.status,
      totalLiquidity: position.reserve.totalLiquidity,
      isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetSymbol === canonicalSymbol,
      supplyCap: position.reserve.supplyCap,
    },
    user: {
      balance: walletInfo.findWalletBalanceForSymbol(canonicalSymbol),
    },
    chain: {
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  })

  return {
    selectedAsset: {
      token: marketInfo.findOneTokenBySymbol(canonicalSymbol),
      value,
      balance: walletInfo.findWalletBalanceForSymbol(canonicalSymbol),
    },
    maxSelectedFieldName: 'isMaxSelected',
    changeAsset,
    maxValue,
  }
}
